/**
 * IAMONEAI useStreamingChat Hook
 * React hook for streaming chat with token-by-token updates
 * 
 * @version 3.0
 * 
 * Usage:
 * const { 
 *   sendMessage, 
 *   currentResponse, 
 *   isTyping, 
 *   metadata,
 *   error,
 *   abort 
 * } = useStreamingChat({ iin: '20AA-2512-8A46-0988' });
 */

import { useState, useCallback, useRef } from 'react';
import { streamMessage, isStreamingSupported } from '../services/streamingService';

/**
 * Hook for streaming chat functionality
 * 
 * @param {Object} options
 * @param {string} options.iin - User's IIN (required)
 * @param {function} options.onMessageComplete - Callback when message is complete
 * @param {function} options.onError - Callback for errors
 */
export function useStreamingChat({ iin, onMessageComplete, onError }) {
  // State
  const [currentResponse, setCurrentResponse] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState(null);
  
  // Ref for abort function
  const abortRef = useRef(null);

  /**
   * Send a message and stream the response
   */
  const sendMessage = useCallback(async (text) => {
    if (!iin) {
      const err = new Error('IIN is required');
      setError(err);
      onError?.(err);
      return;
    }

    // Check streaming support
    if (!isStreamingSupported()) {
      const err = new Error('Streaming not supported in this browser');
      setError(err);
      onError?.(err);
      return;
    }

    // Reset state
    setCurrentResponse('');
    setMetadata(null);
    setError(null);
    setIsTyping(true);

    try {
      // Start streaming
      const abort = await streamMessage({
        text,
        iin,
        
        onStart: (data) => {
          console.log('Stream started:', data);
        },
        
        onToken: (token, fullResponse) => {
          setCurrentResponse(fullResponse);
        },
        
        onComplete: (data) => {
          setIsTyping(false);
          setMetadata(data);
          onMessageComplete?.({
            text: data.fullResponse,
            intention: data.intention,
            emotion: data.emotion,
            context: data.context,
            memories_saved: data.memories_saved,
            scheduled: data.scheduled,
            timing: data.timing,
          });
        },
        
        onError: (err) => {
          setIsTyping(false);
          setError(err);
          onError?.(err);
        },
      });

      abortRef.current = abort;

    } catch (err) {
      setIsTyping(false);
      setError(err);
      onError?.(err);
    }
  }, [iin, onMessageComplete, onError]);

  /**
   * Abort the current stream
   */
  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      setIsTyping(false);
    }
  }, []);

  /**
   * Clear the current response
   */
  const clear = useCallback(() => {
    setCurrentResponse('');
    setMetadata(null);
    setError(null);
  }, []);

  return {
    // Actions
    sendMessage,
    abort,
    clear,
    
    // State
    currentResponse,
    isTyping,
    metadata,
    error,
    
    // Computed
    hasResponse: currentResponse.length > 0,
    isComplete: metadata !== null && !isTyping,
  };
}

export default useStreamingChat;
